import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Droplets, Sparkles, PenTool, Layers } from 'lucide-react';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';

const BathroomsPage = () => {
  const features = [
    {
      icon: Droplets,
      title: 'Custom Showers',
      description: 'Walk-in showers, frameless glass, rainfall heads, and premium tile work designed for your space.',
    },
    {
      icon: Sparkles,
      title: 'Luxury Finishes',
      description: 'High-end fixtures, marble countertops, and designer hardware for a sophisticated look.',
    },
    {
      icon: PenTool,
      title: 'Custom Vanities',
      description: 'Built-to-fit vanities with quality cabinetry, undermount sinks, and ample storage.',
    },
    {
      icon: Layers,
      title: 'Tile & Flooring',
      description: 'Expert tile installation including heated floors, accent walls, and waterproofing.',
    },
  ];

  const benefits = [
    'Increase your home value',
    'Modern, spa-like designs',
    'Quality materials and fixtures',
    'Expert waterproofing',
    'Custom storage solutions',
    'Efficient project timelines',
  ];

  // Real project photos
  const galleryImages = [
    {
      url: 'https://customer-assets.emergentagent.com/job_built-to-last-4/artifacts/n22zq6v8_1000068955.jpg',
      title: 'Marble Master Bathroom',
      alt: 'Calgary bathroom renovation with marble tile and freestanding tub - bathroom contractors Calgary'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_built-to-last-4/artifacts/k340dda3_1000069049.jpg',
      title: 'Luxury Spa Bathroom',
      alt: 'Custom bathroom renovation Calgary with terrazzo tile and modern fixtures'
    },
  ];

  return (
    <div className="min-h-screen" data-testid="bathrooms-page">
      <Navigation />
      
      {/* Hero Section - Using real bathroom photo */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_built-to-last-4/artifacts/n22zq6v8_1000068955.jpg"
            alt="Calgary bathroom renovation with marble tile and freestanding soaker tub"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0f172a]/60" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full">
          <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
            Our Services
          </p>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
            Bathroom Renovation Calgary
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Transform your bathroom into a luxurious retreat. Custom designs, 
            premium finishes, and expert craftsmanship for Calgary homeowners.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
                Bathroom Renovation Calgary
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a] mb-6">
                Luxury Bathroom Renovations
              </h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed">
                Your bathroom should be a sanctuary. At YYC Custom Basement & Bath, we create 
                stunning bathroom spaces that combine beautiful design with lasting functionality.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                From modern minimalist designs to classic elegance, we work with you to create 
                a bathroom that reflects your style and meets your daily needs. Our attention to 
                waterproofing and proper ventilation ensures your renovation stands the test of time.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {benefits.slice(0, 4).map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-[#c5a059] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Real photos side by side */}
            <div className="grid grid-cols-1 gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_built-to-last-4/artifacts/k340dda3_1000069049.jpg"
                alt="Luxury spa bathroom renovation Calgary with terrazzo tile and wood slat wall"
                className="w-full h-80 object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-28 bg-[#f8f9fa]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
              What We Offer
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a]">
              Bathroom Renovation Services
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 border border-slate-100 hover:border-slate-200 transition-all hover:-translate-y-1 hover:shadow-lg"
                data-testid={`bathroom-feature-${index}`}
              >
                <feature.icon size={32} className="text-[#c5a059] mb-4" />
                <h3 className="font-heading text-xl font-semibold text-[#0f172a] mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery - Real Photos */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
                Our Work
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a]">
                Real Projects Completed in Calgary
              </h2>
            </div>
            <Link
              to="/gallery"
              className="hidden md:flex items-center gap-2 text-[#0f172a] font-medium hover:text-[#c5a059] transition-colors"
            >
              View All
              <ArrowRight size={18} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {galleryImages.map((image, index) => (
              <div key={index} className="group relative overflow-hidden aspect-[4/3]">
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-[#0f172a]/0 group-hover:bg-[#0f172a]/40 transition-all flex items-end p-6">
                  <span className="text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    {image.title}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#0f172a]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            Ready to Renovate Your Bathroom?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Get a free estimate for your bathroom renovation project. 
            Let's discuss your vision for the perfect bathroom.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-[#c5a059] text-[#0f172a] px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-white transition-colors"
            data-testid="bathroom-cta-button"
          >
            Request Free Estimate
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BathroomsPage;
